import React from "react";
import { Text } from "react-native";
import Colors from "../../../../Resources/Color";

/**
 * Highlighted text component that underlines matching phrases
 * Uses greedy longest-match-first algorithm for phrase matching
 */
export const HighlightedText = ({ text, searchQuery }) => {
  if (!searchQuery || !searchQuery.trim()) {
    return (
      <Text style={{ fontSize: 13, color: Colors.blackColor }}>{text}</Text>
    );
  }

  // Normalize function for comparison (case and accent insensitive)
  const normalize = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const normalizedText = normalize(text);
  const normalizedQuery = normalize(searchQuery.trim());

  // Generate all possible substrings from the query (sorted by length, longest first)
  const generateQuerySubstrings = (query) => {
    const substrings = [];
    const words = query.split(/\s+/);

    // Generate all contiguous subsequences of words
    for (let length = words.length; length >= 1; length--) {
      for (let start = 0; start <= words.length - length; start++) {
        const substring = words.slice(start, start + length).join(" ");
        if (substring) {
          substrings.push(substring);
        }
      }
    }

    return substrings;
  };

  // Important: ensure longer matches win over shorter prefixes.
  // Example: query contains "bar" and "barbell"; we must match "barbell" first
  // so that "bar" doesn't consume the overlap and prevent highlighting "bell".
  const querySubstrings = generateQuerySubstrings(normalizedQuery).sort(
    (a, b) => {
      const aWordCount = a.split(/\s+/).length;
      const bWordCount = b.split(/\s+/).length;

      if (bWordCount !== aWordCount) {
        return bWordCount - aWordCount;
      }

      if (b.length !== a.length) {
        return b.length - a.length;
      }

      return a.localeCompare(b);
    }
  );

  // Track which character positions should be underlined
  const underlinePositions = new Set();

  // Track which query substrings have been matched (to avoid duplicate matches)
  const matchedSubstrings = new Set();

  // Greedy matching: try longest matches first
  for (const substring of querySubstrings) {
    if (matchedSubstrings.has(substring)) {
      continue;
    }

    // Find all occurrences of this substring in the text
    let searchIndex = 0;
    while (searchIndex < normalizedText.length) {
      const foundIndex = normalizedText.indexOf(substring, searchIndex);

      if (foundIndex === -1) {
        break;
      }

      // Check if this range overlaps with already matched positions
      let hasOverlap = false;
      for (let i = foundIndex; i < foundIndex + substring.length; i++) {
        if (underlinePositions.has(i)) {
          hasOverlap = true;
          break;
        }
      }

      // If no overlap, mark this range as matched
      if (!hasOverlap) {
        for (let i = foundIndex; i < foundIndex + substring.length; i++) {
          underlinePositions.add(i);
        }
        matchedSubstrings.add(substring);
      }

      searchIndex = foundIndex + 1;
    }
  }

  // Build the text with underlined characters
  return (
    <Text style={{ fontSize: 13, color: Colors.blackColor }}>
      {text.split("").map((char, index) => {
        if (underlinePositions.has(index)) {
          return (
            <Text
              key={index}
              style={{
                textDecorationLine: "underline",
                textDecorationColor: Colors.bubbleColor,
                textDecorationStyle: "solid",
              }}
            >
              {char}
            </Text>
          );
        }
        return <Text key={index}>{char}</Text>;
      })}
    </Text>
  );
};
